import React,{useState,useEffect} from 'react';
import { withRouter } from "../utils/router";
import { connect } from "react-redux";
import { setError } from "../store/features/error/errorSlice";
import { makeStyles } from 'tss-react/mui';
import {
  Paper,
  Grid,
  Switch,
  Select,
  Button,
  MenuItem,
  Typography
} from "@mui/material";
import CallMissedIcon from "@mui/icons-material/CallMissedOutlined";
import VoicemailIcon from "@mui/icons-material/VoicemailOutlined";
import FaxIcon from "@mui/icons-material/FaxOutlined";
import RecordingIcon from "@mui/icons-material/GraphicEqOutlined";
import SmsIcon from "@mui/icons-material/SmsOutlined";
import LanguageIcon from "@mui/icons-material/LanguageOutlined";
import TraceIcon from "@mui/icons-material/TerminalOutlined";
import { setUser } from "../store/features/user/userSlice";
import { updateUserWebappSettings, getUserProfileData } from "../utils/utils";
import HelpMenu from "../components/helpMenu";
import { sipTraceAvailable, isSipTraceEnabled, setSipTraceEnabled } from "../utils/sipTrace";

const mapDispatchToProps = {
  setError,
  setUser,
};

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "inherit",
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.up("md")]: {
      // The card can be taller than a short window.
      overflowY: "auto",
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
    },
  },
  gridContainer: {
    height: "inherit",
    padding: theme.spacing(4),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4, 6),
    },
  },
  title: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      marginBottom: theme.spacing(4),
    },
    fontSize: 60,
    fontWeight: 300,
  },
  gridItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: "inherit",
    width: "100%",
  },
  paper: {
    width: "100%",
    maxWidth: 640,
    borderRadius: 12,
    overflow: "hidden",
  },
  section: {
    padding: theme.spacing(2.5, 3, 1.5),
    "& + &": {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },
  sectionHint: {
    fontSize: 13,
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  // One setting: icon, label with an optional explanation, and its control.
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minHeight: 48,
    padding: theme.spacing(0.5, 0),
    cursor: "pointer",
  },
  rowStatic: {
    cursor: "default",
  },
  rowIcon: {
    flexShrink: 0,
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    "& svg": {
      fontSize: 18,
    },
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  rowLabel: {
    fontSize: 14.5,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },
  rowHint: {
    fontSize: 12.5,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  select: {
    minWidth: 160,
    fontSize: 14.5,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
  version: {
    fontSize: 12.5,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    marginLeft: "auto",
  },
  textButton: {
    textTransform: "none",
    color: theme.palette.text.secondary,
  },
  primary: {
    textTransform: "none",
    minWidth: 128,
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      filter: "brightness(0.93)",
      boxShadow: "none",
    },
  },
}));


const AppSettingsView = (props) => {
  const { classes, cx } = useStyles();
  const { settings, branding, session, setError, setUser, voipActive } = props;
  // SIP traces live in this browser only (not in the settings saved on the
  // server). The checkbox is a pending choice that the Update button applies.
  const [appliedSipTrace, setAppliedSipTrace] = useState(isSipTraceEnabled());
  const [sipTrace, setSipTrace] = useState(appliedSipTrace);

  const handleSipTraceChange = (event) => {
    setSipTrace(event.target.checked);
  };

  const serverSettingsChanged = () =>
    language !== settings.language ||
    notificationSettings.call !== settings.notifications.call ||
    notificationSettings.fax !== settings.notifications.fax ||
    notificationSettings.voicemail !== settings.notifications.voicemail ||
    notificationSettings.recording !== settings.notifications.recording ||
    notificationSettings.conversation !== settings.notifications.conversation;
  const [language,setLanguage] = useState(settings.language);
  const [notificationSettings, setNotificationSettings] = useState(settings.notifications);
  const [formChanged, setFormChanged] = useState(false);
  const { call, fax, voicemail, recording, conversation } = notificationSettings;

  // eslint-disable-next-line
  useEffect(() => {
    setFormChanged(serverSettingsChanged() || sipTrace !== appliedSipTrace);
  })

  const handleChange = (event) => {
    setNotificationSettings({
      ...notificationSettings,
      [event.target.name]: event.target.checked?"yes":"no",
    });
  };

  const handleSelectChange = (event) => {
    setLanguage(event.target.value);
  }
   
  // Turns the traces on or off and tells the user where to look: an idle
  // phone sends nothing, so the console stays empty until the next call.
  const applySipTrace = () => {
    const enabled = setSipTraceEnabled(sipTrace);
    setAppliedSipTrace(enabled);
    setSipTrace(enabled);
    if (enabled) {
      console.info("[SIP Traces] ON. SIP messages of the browser phone are logged here, prefixed with \"JsSIP:\". They appear with the next call or registration; reload the page to capture the registration.");
    } else {
      console.info("[SIP Traces] OFF.");
    }
    setError({
      visible: true,
      type: "info",
      message: enabled
        ? "SIP traces are on. Open the browser console (F12). Traces appear with the next call; reload the page to capture the registration."
        : "SIP traces are off.",
    });
  };

  const submitSettings = async () => {
    if (sipTrace !== appliedSipTrace) {
      applySipTrace();
    }
    // Nothing to save on the server when only the SIP traces changed.
    if (!serverSettingsChanged()) {
      return;
    }
    let values = {
      token: session.token,
      server: session.lastServer,
      settings: {
        language: language,
        notifications: notificationSettings,
      },
    };
    try {
      await updateUserWebappSettings(values);

      try {
        await getUserProfileData(
          {
            token: session.token,
            server: session.lastServer,
          },setUser
        );
        setError({
          visible: true,
          type: "success",
          message: "Settings saved.",
        });
      } catch (error) {
        setError({
          visible: true,
          type: "error",
          message: error,
        });
      }
    } catch (error) {
      setError({
        visible: true,
        type: "error",
        message: error,
      });
    }
  }

  // Puts back what is saved, dropping the changes that were not applied.
  const discardChanges = () => {
    setLanguage(settings.language);
    setNotificationSettings(settings.notifications);
    setSipTrace(appliedSipTrace);
  };

  const renderSwitchRow = (param, paramOnChange, name, icon, label, hint) => {
    return (
      <label className={classes.row} key={name}>
        <span className={classes.rowIcon}>{icon}</span>
        <span className={classes.rowText}>
          <div className={classes.rowLabel}>{label}</div>
          {hint && <div className={classes.rowHint}>{hint}</div>}
        </span>
        <Switch
          color="primary"
          checked={param === "yes" ? true : false}
          onChange={paramOnChange}
          name={name}
        />
      </label>
    )
  }

  return (
    <div className={classes.root}>
      <HelpMenu />
      <Grid
        container
        direction="column"
        className={classes.gridContainer}
        sx={{
          alignItems: "center",
          justifyContent: "center"
        }}>
        <Grid className={classes.gridItem}>
          <Typography
            variant="h4"
            component="div"
            className={classes.title}
            sx={{
              color: "inherit"
            }}
          >
            Settings
          </Typography>
          <Paper className={classes.paper}>
            <div className={classes.section}>
              <div className={classes.sectionTitle}>Notifications</div>
              <div className={classes.sectionHint}>
                Choose what you are alerted about in the notifications menu.
              </div>
              {renderSwitchRow(call, handleChange, "call", <CallMissedIcon />, "Missed calls")}
              {renderSwitchRow(voicemail, handleChange, "voicemail", <VoicemailIcon />, "New voicemails")}
              {renderSwitchRow(fax, handleChange, "fax", <FaxIcon />, "New faxes")}
              {renderSwitchRow(recording, handleChange, "recording", <RecordingIcon />, "New call recordings")}
              {renderSwitchRow(conversation, handleChange, "conversation", <SmsIcon />, "New SMS messages")}
            </div>
            <div className={classes.section}>
              <div className={classes.sectionTitle}>Language</div>
              <div className={cx(classes.row, classes.rowStatic)}>
                <span className={classes.rowIcon}><LanguageIcon /></span>
                <span className={classes.rowText}>
                  <div className={classes.rowLabel}>Display language</div>
                  <div className={classes.rowHint}>More languages are coming.</div>
                </span>
                <Select
                  value={language}
                  name="language"
                  size="small"
                  className={classes.select}
                  onChange={(e) => handleSelectChange(e)}
                  inputProps={{ "aria-label": "display language" }}
                >
                  <MenuItem value="english">English</MenuItem>
                  <MenuItem value="german" disabled>
                    German
                  </MenuItem>
                  <MenuItem value="greek" disabled>
                    Greek
                  </MenuItem>
                  <MenuItem value="spanish" disabled>
                    Spanish
                  </MenuItem>
                </Select>
              </div>
            </div>
            {voipActive && sipTraceAvailable() && (
              <div className={classes.section}>
                <div className={classes.sectionTitle}>Troubleshooting</div>
                {renderSwitchRow(
                  sipTrace ? "yes" : "no",
                  handleSipTraceChange,
                  "sipTrace",
                  <TraceIcon />,
                  "SIP traces",
                  "Logs the browser phone's SIP messages to this browser's console (F12)."
                )}
              </div>
            )}
            <div className={classes.footer}>
              <div className={classes.version}>
                <div>Version {branding.version}</div>
                {branding.lastUpdate ? <div>Latest patch: {branding.lastUpdate}</div> : null}
              </div>
              <div className={classes.actions}>
                {formChanged && (
                  <Button className={classes.textButton} onClick={discardChanges}>
                    Discard
                  </Button>
                )}
                <Button
                  variant="contained"
                  disabled={!formChanged}
                  onClick={submitSettings}
                  className={classes.primary}
                >
                  Update
                </Button>
              </div>
            </div>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    settings: state.user.webapp,
    session: state.session,
    branding: state.branding,
    voipActive: !!(state.user.voip && state.user.voip.active === "yes"),
  };
};
export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AppSettingsView)
);
